/**
 * Tibbs's playback: a queue of voiced chunks that arrive one by one while the
 * rest of the readout is still being written and voiced (Single Didge's
 * voice.playQueue), played on the game's one AudioContext. Each chunk is
 * decoded as it lands and scheduled gaplessly after the last; the
 * presentation adds the Largo under him (Single Didge's ceremony envelope:
 * music alone, a duck under the voice, a half-sine tail after the last word).
 *
 * Captions follow the playhead: inside each chunk the sentence on screen is
 * the one the clock has reached, weighting sentences by length and cue pauses.
 *
 * iOS: the page's audio session is "ambient" (effects mix with the player's
 * music and obey the silent switch). While Tibbs talks it is held at
 * "playback", so he is heard with the silent switch on, then handed back.
 * Effects duck under him.
 *
 * Muted (the top-bar sound switch), volume 0, or no audio at all: the same
 * queue runs on a wall clock with captions only, slow enough to read.
 */
import { holdPlaybackSession } from '../lib/audioKeeper';
import { audioContext, duckEffects, unlockAudio } from '../lib/sound';
import { captionText } from '../../readout/script';
import { tightenBuffer } from './pace';

export type PlayState = 'loading' | 'playing' | 'blocked' | 'done' | 'stopped';

export interface QueueItem {
  /** The chunk's MP3: bytes in hand (a vs-computer stream) or a URL (a room). */
  bytes?: Uint8Array | ArrayBuffer;
  url?: string;
  /** Its words, with cues, for captions. */
  text: string;
}

export interface Progress {
  /** Chunk and sentence on screen, and the sentence's index across the whole readout. */
  chunk: number;
  sentence: string;
  globalSentence: number;
}

export interface QueueOpts {
  /** Linear voice gain (settings and mute); 0 plays captions only. */
  gain: number;
  /** Start this many ms from now (an online table starts together). */
  delayMs?: number;
  music?: { url: string; soloS: number } | null;
  onState: (s: PlayState) => void;
  onProgress?: (p: Progress) => void;
}

export interface QueueCtl {
  push(item: QueueItem): void;
  /** No more chunks are coming. */
  end(): void;
  stop(): void;
  /** A tap after 'blocked' (audio was locked): start now. */
  resume(): void;
  setGain(g: number): void;
}

/** Ceremony mix, after Single Didge's ceremony-mix.js: the music ducks to −16 dB under the voice over 0.6 s and fades on a half-sine 2.5 s after the last word. */
export const DUCK = Math.pow(10, -16 / 20);
export const DUCK_S = 0.6;
export const TAIL_S = 2.5;
const FADE_S = 0.12;
const START_LEAD_S = 0.15;
const MUSIC_WAIT_MS = 8000;

// ---------------------------------------------------------------------------
// Captions: sentences of a chunk, weighted by how long they take to say.

const CUE_SECONDS: Record<string, number> = { beat: 0.15, pause: 0.3, 'long pause': 0.5 }; // as worker/readout.ts PAUSE_CUES

/** A chunk's spoken sentences with relative weights (characters, plus cue pauses as characters). */
export function sentencesOf(text: string): { text: string; weight: number }[] {
  const out: { text: string; weight: number }[] = [];
  const parts = String(text ?? '').match(/[^.!?…]*[.!?…]+["')\]]*\s*|[^.!?…]+$/g) ?? [];
  let carry = 0;
  for (const p of parts) {
    let w = 0;
    for (const m of p.matchAll(/\[([^\]]*)\]/g)) w += (CUE_SECONDS[m[1]!.trim().toLowerCase()] ?? 0) * 14;
    const t = captionText(p);
    if (!t) {
      carry += w;
      continue;
    }
    out.push({ text: t, weight: carry + w + t.length + 6 });
    carry = 0;
  }
  return out;
}

/** Which sentence of a chunk is being said `fraction` (0..1) of the way through it. */
export function sentenceAt(sentences: { weight: number }[], fraction: number): number {
  const total = sentences.reduce((a, s) => a + s.weight, 0);
  if (!total) return 0;
  let acc = 0;
  const at = Math.max(0, Math.min(1, fraction)) * total;
  for (let i = 0; i < sentences.length; i++) {
    acc += sentences[i]!.weight;
    if (at < acc) return i;
  }
  return sentences.length - 1;
}

/** Seconds to leave a caption up when there is no voice (about 14 characters a second, at least 1.6 s). */
export function readingTime(text: string): number {
  return Math.max(1.6, captionText(text).length / 14);
}

// ---------------------------------------------------------------------------
// Decoding

function decode(c: AudioContext, bytes: ArrayBuffer): Promise<AudioBuffer> {
  return new Promise((res, rej) => {
    try {
      const p = c.decodeAudioData(bytes, res, rej);
      p?.then?.(res, rej);
    } catch (e) {
      rej(e);
    }
  });
}

const musicCache = new WeakMap<AudioContext, Promise<AudioBuffer | null>>();
let musicBytes: Promise<ArrayBuffer | null> | null = null;
const fetchBytes = (url: string) =>
  fetch(url)
    .then((r) => (r.ok ? r.arrayBuffer() : null))
    .catch(() => null);

/** Fetch (and decode, once audio is unlocked) the ceremony music while the presentation is being written. */
export function warmMusic(url: string): void {
  const c = audioContext();
  if (c) void musicBuffer(c, url);
  else if (!musicBytes) musicBytes = fetchBytes(url);
}

function musicBuffer(c: AudioContext, url: string): Promise<AudioBuffer | null> {
  const hit = musicCache.get(c);
  if (hit) return hit;
  const p = (musicBytes ?? fetchBytes(url)).then((b) => (b ? decode(c, b.slice(0)) : null)).catch(() => null);
  musicBytes = null;
  musicCache.set(c, p);
  p.then((b) => {
    if (!b) musicCache.delete(c);
  });
  return p;
}

async function bytesOf(item: QueueItem): Promise<ArrayBuffer | null> {
  if (item.bytes) {
    const b = item.bytes instanceof Uint8Array ? item.bytes : new Uint8Array(item.bytes);
    return b.slice().buffer as ArrayBuffer;
  }
  if (item.url) return fetchBytes(item.url);
  return null;
}

async function running(c: AudioContext, ms = 600): Promise<boolean> {
  if (c.state === 'running') return true;
  try {
    await Promise.race([Promise.resolve(c.resume()), new Promise((r) => setTimeout(r, ms))]);
  } catch {
    /* ignore */
  }
  return (c.state as string) === 'running';
}

// ---------------------------------------------------------------------------
// The queue

let current: { stop: () => void } | null = null;

/** Stop whatever Tibbs is saying (Next hole, leaving the table). */
export function stopTibbs(): void {
  current?.stop();
}

export function playQueue(opts: QueueOpts): QueueCtl {
  current?.stop();
  const items: QueueItem[] = [];
  const decoded: (Promise<AudioBuffer | null> | null)[] = [];
  let ended = false;
  let finished = false;
  let stopped = false;
  let gain = opts.gain;
  let poll: ReturnType<typeof setInterval> | null = null;
  const timers: ReturnType<typeof setTimeout>[] = [];
  let release: () => void = () => {};
  let teardown: (() => void) | null = null;
  let voiceGainNode: GainNode | null = null;
  let blockedStart: (() => void) | null = null;
  let wake: (() => void) | null = null;
  const c = gain > 0 ? audioContext() : null;
  const startAt = Date.now() + Math.max(0, opts.delayMs ?? 0);
  // Sentence index offsets per chunk, so captions can count across the whole readout.
  const sentenceCounts: number[] = [];
  const offsetOf = (chunk: number) => sentenceCounts.slice(0, chunk).reduce((a, n) => a + n, 0);
  let lastReported = '';
  const report = (chunk: number, idx: number, sentences: { text: string }[]) => {
    const s = sentences[idx];
    if (!s) return;
    const id = `${chunk}:${idx}`;
    if (id === lastReported) return;
    lastReported = id;
    opts.onProgress?.({ chunk, sentence: s.text, globalSentence: offsetOf(chunk) + idx });
  };

  const finish = (state: 'done' | 'stopped') => {
    if (finished) return;
    finished = true;
    timers.forEach(clearTimeout);
    if (poll) clearInterval(poll);
    teardown?.();
    release();
    duckEffects(false);
    if (current === handle) current = null;
    opts.onState(state);
  };

  const handle: QueueCtl = {
    push(item) {
      if (finished) return;
      items.push(item);
      sentenceCounts.push(sentencesOf(item.text).length);
      // Carl's long pauses are shortened as each chunk is decoded (pace.ts): the readout's pace lives here.
      if (c) decoded.push(bytesOf(item).then((b) => (b ? decode(c, b).then((buf) => tightenBuffer(c, buf)).catch(() => null) : null)));
      else decoded.push(null);
      wake?.();
    },
    end() {
      ended = true;
      wake?.();
    },
    stop() {
      if (stopped || finished) return;
      stopped = true;
      finish('stopped');
    },
    resume() {
      unlockAudio();
      const go = blockedStart;
      blockedStart = null;
      go?.();
    },
    setGain(g) {
      gain = g;
      if (voiceGainNode && c) {
        voiceGainNode.gain.cancelScheduledValues(c.currentTime);
        voiceGainNode.gain.setTargetAtTime(g, c.currentTime, 0.05);
      }
    },
  };
  current = handle;
  opts.onState('loading');

  const waitForItem = (i: number) =>
    new Promise<boolean>((resolve) => {
      const check = () => {
        if (finished) return resolve(false);
        if (items.length > i) return resolve(true);
        if (ended) return resolve(false);
        wake = check;
      };
      check();
    });

  if (!c) {
    // Captions only: a wall clock, each sentence up long enough to read.
    void (async () => {
      const wait = startAt - Date.now();
      if (wait > 0) await new Promise((r) => timers.push(setTimeout(r, wait)));
      if (finished) return;
      opts.onState('playing');
      for (let i = 0; ; i++) {
        if (!(await waitForItem(i))) break;
        const sentences = sentencesOf(items[i]!.text);
        for (let k = 0; k < sentences.length; k++) {
          if (finished) return;
          report(i, k, sentences);
          await new Promise((r) => timers.push(setTimeout(r, readingTime(sentences[k]!.text) * 1000)));
        }
      }
      if (!finished) finish('done');
    })();
    return handle;
  }

  void (async () => {
    if (!(await waitForItem(0))) return finish('done');
    const musicP = opts.music ? musicBuffer(c, opts.music.url) : Promise.resolve(null);
    const first = await decoded[0]!;
    const music = opts.music ? await Promise.race([musicP, new Promise<null>((r) => setTimeout(() => r(null), MUSIC_WAIT_MS))]) : null;
    if (finished) return;
    const wait = startAt - Date.now();
    if (wait > 0) await new Promise((r) => timers.push(setTimeout(r, wait)));
    if (finished) return;
    if (!first && items.length === 1 && ended) return finish('done');

    const begin = () => {
      if (finished) return;
      release = holdPlaybackSession();
      duckEffects(true);
      const out = c.createGain();
      out.gain.value = 1;
      out.connect(c.destination);
      voiceGainNode = c.createGain();
      voiceGainNode.gain.value = gain;
      voiceGainNode.connect(out);
      const srcs: AudioBufferSourceNode[] = [];
      const t0 = c.currentTime + START_LEAD_S;
      const solo = music && opts.music ? opts.music.soloS : 0;
      const voiceStart = t0 + solo;
      let cursor = voiceStart;
      const schedule: { start: number; dur: number; chunk: number }[] = [];
      let musicEnv: GainNode | null = null;
      let musicSrc: AudioBufferSourceNode | null = null;
      if (music) {
        musicEnv = c.createGain();
        musicEnv.connect(voiceGainNode);
        const g = musicEnv.gain;
        g.setValueAtTime(1, t0);
        g.setValueAtTime(1, voiceStart);
        g.linearRampToValueAtTime(DUCK, voiceStart + DUCK_S);
        musicSrc = c.createBufferSource();
        musicSrc.buffer = music;
        musicSrc.connect(musicEnv);
        musicSrc.start(t0);
        srcs.push(musicSrc);
      }
      let tailAt: number | null = null;
      const scheduleTail = () => {
        if (tailAt !== null) return;
        tailAt = cursor;
        if (musicEnv && music && musicSrc) {
          const g = musicEnv.gain;
          const at = Math.max(cursor, c.currentTime + 0.05);
          g.cancelScheduledValues(at);
          g.setValueAtTime(DUCK, at);
          const curve = new Float32Array(50).map((_, i) => DUCK * Math.cos((Math.PI / 2) * (i / 49)));
          try {
            g.setValueCurveAtTime(curve, at, TAIL_S);
          } catch {
            g.linearRampToValueAtTime(0, at + TAIL_S);
          }
          try {
            musicSrc.stop(Math.min(t0 + music.duration, at + TAIL_S + 0.05));
          } catch {
            /* ignore */
          }
          tailAt = Math.min(t0 + music.duration, at + TAIL_S);
        }
      };
      let next = 0;
      const pump = async () => {
        while (!finished) {
          if (next >= items.length) {
            if (ended) {
              scheduleTail();
              return;
            }
            if (!(await waitForItem(next))) {
              scheduleTail();
              return;
            }
          }
          const buf = await decoded[next]!;
          if (finished) return;
          if (buf) {
            const src = c.createBufferSource();
            src.buffer = buf;
            src.connect(voiceGainNode!);
            const at = Math.max(cursor, c.currentTime + 0.05);
            src.start(at);
            srcs.push(src);
            schedule.push({ start: at, dur: buf.duration, chunk: next });
            cursor = at + buf.duration;
          }
          next++;
        }
      };
      void pump();
      teardown = () => {
        const now = c.currentTime;
        try {
          out.gain.cancelScheduledValues(now);
          out.gain.setValueAtTime(out.gain.value, now);
          out.gain.linearRampToValueAtTime(0, now + FADE_S);
        } catch {
          /* ignore */
        }
        setTimeout(() => {
          for (const s of srcs) {
            try {
              s.stop();
            } catch {
              /* already stopped */
            }
          }
          try {
            out.disconnect();
          } catch {
            /* ignore */
          }
        }, FADE_S * 1000 + 30);
      };
      opts.onState('playing');
      poll = setInterval(() => {
        const now = c.currentTime;
        const seg = [...schedule].reverse().find((s) => now >= s.start - 0.02);
        if (seg) {
          const sentences = sentencesOf(items[seg.chunk]!.text);
          report(seg.chunk, sentenceAt(sentences, (now - seg.start) / Math.max(0.01, seg.dur)), sentences);
        }
        if (tailAt !== null && now >= Math.max(tailAt, cursor) + 0.1) finish('done');
      }, 50);
    };
    if (await running(c)) begin();
    else {
      opts.onState('blocked');
      blockedStart = () => {
        void running(c, 1500).then((ok) => (ok ? begin() : finish('stopped')));
      };
    }
  })();
  return handle;
}
